import React, { useMemo } from 'react';
import AssistantBotRow from '../shells/AssistantBotRow';
import UserReplyRow from '../shells/UserReplyRow';
import Vc008UploadPreviewRow from './Vc008UploadPreviewRow';
import { buildVariousCopyFileUrl } from './careerUpConfirmationApi';
import { stepFootnoteKey, stepQuestionKey, uploadPromptKey } from './careerUpConfirmationSteps';
import { VariousCopyDocCodes } from '../../../../../catalog/variousCopyDocCodes';
import type { useCareerUpConfirmationFlow } from './useCareerUpConfirmationFlow';
import type { CareerUpConfirmationStep } from './careerUpConfirmationTypes';

type Flow = ReturnType<typeof useCareerUpConfirmationFlow>;

function UploadPreviewBlock({
  fileName,
  previewUrl,
  previewLabel,
  openLabel,
}: {
  fileName: string;
  previewUrl: string;
  previewLabel: string;
  openLabel: string;
}) {
  return (
    <Vc008UploadPreviewRow
      fileName={fileName}
      previewUrl={previewUrl}
      previewLabel={previewLabel}
      openLabel={openLabel}
    />
  );
}

function QuestionBlock({
  stepId,
  t,
  footnote,
  preview,
}: {
  stepId: CareerUpConfirmationStep;
  t: Flow['t'];
  footnote?: string | null;
  preview?: React.ReactNode;
}) {
  return (
    <>
      <AssistantBotRow animateIn>{t(stepQuestionKey(stepId))}</AssistantBotRow>
      {footnote && <AssistantBotRow animateIn>{footnote}</AssistantBotRow>}
      {preview}
    </>
  );
}

export default function CareerUpConfirmationChatThread({
  flow,
  currentEmail,
  invitationId,
}: {
  flow: Flow;
  currentEmail?: string | null;
  invitationId?: string | null;
}) {
  const {
    t,
    committed,
    isComplete,
    noCardDeclined,
    careerUpCardUploadNeeded,
    driversLicenseUploadNeeded,
    currentStep,
    assistantQuestionVisible,
    uploads,
    editingCommitIndex,
  } = flow;
  const undoLabel = t('submitDocuments.chat.undo');
  const editLabel = t('submitDocuments.fillFormsChecklist.edit');

  const email = currentEmail?.trim();
  const inv = invitationId?.trim();

  const vc008PreviewUrl = useMemo(() => {
    if (!email || !inv || !uploads.vc008.hasUpload) return null;
    return buildVariousCopyFileUrl(email, inv, VariousCopyDocCodes.CAREER_UP_RELATED);
  }, [email, inv, uploads.vc008.hasUpload]);

  const vc010PreviewUrl = useMemo(() => {
    if (!email || !inv || !uploads.vc010.hasUpload) return null;
    return buildVariousCopyFileUrl(email, inv, VariousCopyDocCodes.DRIVERS_LICENSE);
  }, [email, inv, uploads.vc010.hasUpload]);

  const committedStepIds = new Set(committed.map((c) => c.stepId));
  const isEditing = editingCommitIndex !== null;
  const isDeclined = noCardDeclined || careerUpCardUploadNeeded || driversLicenseUploadNeeded;

  const renderPreview = (stepId: 'careerUpCardUploaded' | 'driversLicenseUploaded') => {
    if (stepId === 'careerUpCardUploaded' && uploads.vc008.hasUpload && uploads.vc008.fileName && vc008PreviewUrl) {
      return (
        <UploadPreviewBlock
          fileName={uploads.vc008.fileName}
          previewUrl={vc008PreviewUrl}
          previewLabel={t('submitDocuments.careerUpConfirmation.uploadPreviewLabel')}
          openLabel={t('submitDocuments.careerUpConfirmation.openAttachment')}
        />
      );
    }
    if (stepId === 'driversLicenseUploaded' && uploads.vc010.hasUpload && uploads.vc010.fileName && vc010PreviewUrl) {
      return (
        <UploadPreviewBlock
          fileName={uploads.vc010.fileName}
          previewUrl={vc010PreviewUrl}
          previewLabel={t('submitDocuments.careerUpConfirmation.uploadPreviewLabel')}
          openLabel={t('submitDocuments.careerUpConfirmation.openAttachment')}
        />
      );
    }
    return null;
  };

  const showPending = (stepId: CareerUpConfirmationStep) =>
    currentStep?.stepId === stepId &&
    assistantQuestionVisible &&
    (isEditing || !committedStepIds.has(stepId));

  return (
    <>
      {committed.map((c, i) => (
        <React.Fragment key={`${c.stepId}-${i}`}>
          <QuestionBlock
            stepId={c.stepId}
            t={t}
            footnote={c.stepId === 'cardPassword' ? t(stepFootnoteKey('cardPassword')!) : null}
            preview={
              c.stepId === 'careerUpCardUploaded' || c.stepId === 'driversLicenseUploaded'
                ? renderPreview(c.stepId)
                : null
            }
          />
          <UserReplyRow
            displayText={c.displayText}
            showEdit={flow.showEditForCommitIndex(i)}
            onEdit={() => flow.jumpToEditCommitIndex(i)}
            editLabel={editLabel}
            showUndo={flow.showUndoForCommitIndex(i)}
            onUndo={flow.undo}
            undoLabel={undoLabel}
          />
        </React.Fragment>
      ))}

      {noCardDeclined && (
        <AssistantBotRow animateIn>{t('submitDocuments.careerUpConfirmation.noCardMessage')}</AssistantBotRow>
      )}

      {careerUpCardUploadNeeded && (
        <AssistantBotRow animateIn>{t(uploadPromptKey('careerUpCardUploaded'))}</AssistantBotRow>
      )}

      {driversLicenseUploadNeeded && (
        <AssistantBotRow animateIn>{t(uploadPromptKey('driversLicenseUploaded'))}</AssistantBotRow>
      )}

      {!isComplete && !isDeclined && currentStep && (
        <>
          {showPending('hasCard') && <QuestionBlock stepId="hasCard" t={t} />}
          {showPending('careerUpCardUploaded') && (
            <QuestionBlock
              stepId="careerUpCardUploaded"
              t={t}
              preview={renderPreview('careerUpCardUploaded')}
            />
          )}
          {showPending('driversLicenseUploaded') && (
            <QuestionBlock
              stepId="driversLicenseUploaded"
              t={t}
              preview={renderPreview('driversLicenseUploaded')}
            />
          )}
          {showPending('cardPassword') && (
            <QuestionBlock
              stepId="cardPassword"
              t={t}
              footnote={t(stepFootnoteKey('cardPassword')!)}
            />
          )}
        </>
      )}
    </>
  );
}
